/**
 * Sitewide Force Ads — a super-admin-only DB flag (ad_settings.global_force_ads)
 * that forces every ad placement to ignore frequency/cooldown/premium checks
 * for EVERY visitor, not just the admin's own browser. This is the opposite
 * of isAdTestModeOn() in lib/ad-cooldown.ts, which is a localStorage flag
 * scoped to whichever browser toggled it — deliberately so, to keep a normal
 * admin testing session from ever affecting real traffic. Sitewide Force Ads
 * exists for the opposite need: "I need to confirm ads actually render for a
 * real guest / a different account / a phone I'm not logged into," which a
 * per-browser flag can't do at all.
 *
 * isAdTestModeOn() checks this too — see that function — so every existing
 * ad-component call site picks this up automatically with no per-component
 * changes needed.
 *
 * Why this is a localStorage-mirrored cache rather than a plain async fetch:
 * isAdTestModeOn() is called SYNCHRONOUSLY in many places, several of them
 * inside `useState(() => ...)` lazy initializers that run once on mount and
 * are never re-evaluated (NetworkDisplayAd's `canShow`, InFeedVideoAd's
 * `canShow`) — there's no tick available to await a fetch before that first
 * read happens. The mirror means a returning visitor's second-or-later page
 * load has an instant, correct synchronous answer; a page loaded fresh into
 * an empty cache reads `false` on that first render while a background fetch
 * populates the mirror for next time. That's an acceptable gap for an
 * admin-triggered testing tool, not for real per-request correctness.
 */

import { useEffect, useState } from "react"

const CACHE_KEY = "goonru_global_force_ads_cache"
// How long the mirrored value is trusted before a background refetch is
// kicked off — NOT how long stale data blocks anything, since the cached
// value is always returned immediately regardless of age.
const REFRESH_INTERVAL_MS = 30_000

interface CacheEntry {
  enabled: boolean
  fetchedAt: number
}

function readCache(): CacheEntry | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return typeof parsed?.enabled === "boolean" ? parsed : null
  } catch {
    return null
  }
}

function writeCache(enabled: boolean): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ enabled, fetchedAt: Date.now() } satisfies CacheEntry))
    // Same broadcast pattern as cookie-consent.tsx's commit() — lets any
    // mounted component react immediately instead of waiting for its own
    // next poll/render.
    window.dispatchEvent(new Event("global-force-ads-changed"))
  } catch {
    // best-effort
  }
}

let refreshInFlight: Promise<void> | null = null

function refreshInBackground(): void {
  if (refreshInFlight) return
  refreshInFlight = fetch("/api/admin/global-force-ads")
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => {
      if (typeof d?.enabled === "boolean") writeCache(d.enabled)
    })
    .catch(() => {
      // Best-effort — the next call site to check will just retry.
    })
    .finally(() => {
      refreshInFlight = null
    })
}

/** Synchronous read — see the module doc comment for the staleness tradeoff. */
export function isGlobalForceAdsOn(): boolean {
  const cached = readCache()
  if (!cached || Date.now() - cached.fetchedAt > REFRESH_INTERVAL_MS) {
    refreshInBackground()
  }
  return cached?.enabled ?? false
}

/** Admin-only. Throws on failure (including a non-admin caller — enforced server-side). */
export async function setGlobalForceAdsOn(enabled: boolean): Promise<void> {
  const res = await fetch("/api/admin/global-force-ads", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ enabled }),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new Error(body?.error || `Failed to update global Force Ads (HTTP ${res.status})`)
  }
  writeCache(enabled)
}

/** React hook — subscribes to changes made in THIS tab (this file's own writeCache) and other tabs (native `storage` event). */
export function useGlobalForceAdsOn(): boolean {
  const [value, setValue] = useState(() => isGlobalForceAdsOn())
  useEffect(() => {
    const check = () => setValue(isGlobalForceAdsOn())
    check()
    window.addEventListener("global-force-ads-changed", check)
    window.addEventListener("storage", check)
    return () => {
      window.removeEventListener("global-force-ads-changed", check)
      window.removeEventListener("storage", check)
    }
  }, [])
  return value
}
